import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2, CalendarDays } from "lucide-react";
import { routeHead } from "@/components/seo";
import { pageMeta, reviewForm, site } from "@/content";

const ROUTE = "/strategic-finance-review";
export const Route = createFileRoute("/strategic-finance-review")({
  head: () => routeHead(ROUTE),
  component: Page,
});

type Field =
  | { name: string; label: string; type: "text" | "email" | "tel"; required?: boolean }
  | { name: string; label: string; type: "textarea"; required?: boolean }
  | { name: string; label: string; type: "select"; required?: boolean; options: string[] }
  | { name: string; label: string; type: "checkboxGroup"; required?: boolean; options: string[] };

function Page() {
  const p = pageMeta(ROUTE)!;
  const fields = reviewForm.fields as Field[];
  const [values, setValues] = useState<Record<string, string | string[]>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  function setField(name: string, value: string | string[]) {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => ({ ...e, [name]: "" }));
  }

  function toggleCheckbox(name: string, option: string) {
    const current = (values[name] as string[]) || [];
    setField(
      name,
      current.includes(option) ? current.filter((x) => x !== option) : [...current, option],
    );
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    for (const f of fields) {
      if (!f.required) continue;
      const v = values[f.name];
      if (
        !v ||
        (Array.isArray(v) && v.length === 0) ||
        (typeof v === "string" && v.trim() === "")
      ) {
        next[f.name] = "This field is required";
      }
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSending(true);
    setSubmitError(false);
    // Netlify Forms only exists in production; simulate success in local dev.
    if (import.meta.env.DEV) {
      setSubmitted(true);
      setSending(false);
      return;
    }
    const body = new URLSearchParams({ "form-name": "strategic-finance-review", "bot-field": "" });
    for (const f of fields) {
      const v = values[f.name];
      body.set(f.name, Array.isArray(v) ? v.join(", ") : (v ?? ""));
    }
    try {
      // Posts against the static definition in public/__forms.html (Netlify Forms).
      const res = await fetch("/__forms.html", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (!res.ok) throw new Error(`Form submission failed: ${res.status}`);
      setSubmitted(true);
    } catch {
      setSubmitError(true);
    } finally {
      setSending(false);
    }
  }

  if (submitted) {
    return (
      <div className="bg-background text-foreground">
        <section className="hero-enter mx-auto max-w-2xl px-6 py-24 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-teal" aria-hidden />
          <h1 className="mt-6 font-serif text-3xl text-ink sm:text-4xl">
            Thanks. Your review request has been received.
          </h1>
          <p className="mt-4 leading-relaxed text-ink/80">
            Jamee will be in touch shortly to arrange a suitable time. If you would prefer to lock
            in a time now, you can book directly below.
          </p>
          <a
            href={site.contact.bookingUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-8 btn-primary"
          >
            <CalendarDays className="h-4 w-4" /> Book a time with Jamee
          </a>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground">
      <section className="border-b border-line">
        <div className="hero-enter mx-auto max-w-4xl px-6 pt-20">
          <h1 className="mt-4 font-serif text-4xl leading-[1.1] text-ink sm:text-5xl">{p.h1}</h1>
          <p className="mt-6 text-lg leading-relaxed text-ink/80">{reviewForm.intro}</p>
        </div>

        {/* Path 1: book straight into Jamee's calendar.
            Height and width are generous on purpose: Calendly's inline embed has no
            built-in dynamic resize (their own guidance for a widget that needs scrolling
            is simply "increase the height"), and this event's booking form runs to 9
            fields, so a short/narrow box meant the same profile block re-scrolled past
            at every single step, and the date+time+Next layout was cramped enough that
            Next visually overlapped the selected time slot at the old 768px width. */}
        <div className="hero-enter-delay mx-auto max-w-4xl px-6 pt-12">
          <div className="card-premium p-2 shadow-sm">
            <div className="px-6 pb-2 pt-6">
              <h2 className="font-serif text-2xl text-ink">Pick a time with Jamee</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink/80">
                The fastest way to start: a complimentary 30 minute conversation, booked straight
                into the calendar.
              </p>
            </div>
            <iframe
              src={`${site.contact.bookingUrl}?embed_domain=nexterabookkeeping.com.au&embed_type=Inline&hide_gdpr_banner=1&hide_event_type_details=1`}
              title="Book a time with Jamee White, NextEra Bookkeeping"
              className="h-[1180px] w-full rounded-xl border-0"
              loading="lazy"
            />
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 pb-20">
          <div className="mt-14 flex items-center gap-4" aria-hidden>
            <span className="h-px flex-1 bg-[color:var(--mist)]" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[color:var(--subtle)]">
              Or request a callback
            </span>
            <span className="h-px flex-1 bg-[color:var(--mist)]" />
          </div>
          <p className="mt-6 text-[color:var(--ink)]">
            Tell us about your current setup and Jamee will come to the conversation prepared. We
            respond within one business day.
          </p>

          <form onSubmit={onSubmit} noValidate className="mt-10 space-y-6">
            {fields.map((f) => (
              <div key={f.name}>
                <label
                  htmlFor={f.name}
                  className="block text-sm font-medium text-[color:var(--navy)]"
                >
                  {f.label}
                  {f.required && <span className="text-[color:var(--brand)]"> *</span>}
                </label>
                <div className="mt-2">
                  {f.type === "textarea" ? (
                    <textarea
                      id={f.name}
                      rows={4}
                      value={(values[f.name] as string) || ""}
                      onChange={(e) => setField(f.name, e.target.value)}
                      className="w-full rounded-md border border-[color:var(--mist)] bg-white px-4 py-3 text-sm text-[color:var(--navy)] focus:border-[color:var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/30"
                    />
                  ) : f.type === "select" ? (
                    <select
                      id={f.name}
                      value={(values[f.name] as string) || ""}
                      onChange={(e) => setField(f.name, e.target.value)}
                      className="w-full rounded-md border border-[color:var(--mist)] bg-white px-4 py-3 text-sm text-[color:var(--navy)] focus:border-[color:var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/30"
                    >
                      <option value="">Select…</option>
                      {f.options.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  ) : f.type === "checkboxGroup" ? (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {f.options.map((o) => {
                        const current = (values[f.name] as string[]) || [];
                        const checked = current.includes(o);
                        return (
                          <label
                            key={o}
                            className="flex items-start gap-2 rounded-md border border-[color:var(--mist)] bg-white px-3 py-2 text-sm text-[color:var(--ink)]"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleCheckbox(f.name, o)}
                              className="mt-1 h-4 w-4 rounded border-[color:var(--mist)] text-[color:var(--brand)] focus:ring-[color:var(--brand)]"
                            />
                            <span>{o}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <input
                      id={f.name}
                      type={f.type}
                      value={(values[f.name] as string) || ""}
                      onChange={(e) => setField(f.name, e.target.value)}
                      className="w-full rounded-md border border-[color:var(--mist)] bg-white px-4 py-3 text-sm text-[color:var(--navy)] focus:border-[color:var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/30"
                    />
                  )}
                </div>
                {errors[f.name] && <p className="mt-1 text-xs text-destructive">{errors[f.name]}</p>}
              </div>
            ))}

            <button type="submit" disabled={sending} className="btn-primary disabled:opacity-60">
              {sending ? "Sending…" : "Request my Strategic Finance Review"}
            </button>
            {submitError && (
              <p className="text-sm text-destructive">
                Something went wrong sending your request. Please try again, or book a time directly
                in the calendar above.
              </p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}
